import { useState } from 'react';
import {
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Collapse,
  styled,
  Chip,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useUnityBuildStore } from '../../stores/store';
import { useFetchContracts } from '../../hooks/useFetchContracts';
import { useDeleteContractMutation } from '../../hooks/useCreateContractMutation';
import { useStore } from 'zustand';
import { Contract } from '../../api/protosCompiled/contract/contract_pb';
import { AssociatedEstimate } from '../../api/protosCompiled/contract/contract_pb';
import { CreateContract } from './CreateContract';
import { useHandleSetContract, useHandleSetEstimateForChangeOrders } from './helpers';
import { ListChangeOrders } from './ListChangeOrders';
import { ContractBudgetReport } from '../reports/ContractBudgetReport';
import { getStatusChipProps } from './ListEstimates';
import { formatDateForDisplay } from '../../utils/dateUtils';

// Create styled components for consistent dark mode styling
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
  color: theme.palette.text.primary,
  fontWeight: 600,
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3c3c3c !important' : '#e0e0e0 !important',
  },
  '&:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.05)} !important`,
  },
  '& .MuiTableCell-root': {
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
}));

// Helper function to count change orders by status
const getChangeOrderStatusCounts = (changeOrdersList: any[]): Record<string, number> => {
  const statusCounts: Record<string, number> = {};

  changeOrdersList?.forEach(changeOrder => {
    const status = changeOrder.changeorderstatusname || 'Unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  return statusCounts;
};

// Component to display change order status counts compactly
const ChangeOrderStatusDisplay = ({
  changeOrdersList,
  theme,
}: {
  changeOrdersList: any[];
  theme: any;
}) => {
  const statusCounts = getChangeOrderStatusCounts(changeOrdersList);
  const statusEntries = Object.entries(statusCounts);

  if (statusEntries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        None
      </Typography>
    );
  }

  // Create compact status display with abbreviated names and colors
  const getStatusAbbreviation = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'accepted':
        return 'A';
      case 'pending':
        return 'P';
      case 'rejected':
        return 'R';
      case 'draft':
        return 'D';
      case 'submitted':
        return 'S';
      default:
        return status.charAt(0).toUpperCase();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {statusEntries.map(([status, count]) => (
        <Chip
          key={status}
          label={`${getStatusAbbreviation(status)}:${count}`}
          size="small"
          sx={{
            ...getStatusChipProps(status, theme),
            height: '20px',
            fontSize: '0.65rem',
            fontWeight: 600,
            '& .MuiChip-label': {
              px: 0.5,
            },
          }}
        />
      ))}
    </Box>
  );
};

interface ContractRowProps {
  item: Contract.AsObject;
  handleSetValue: (action: 'create' | 'update', contract: Contract.AsObject | null) => void;
  onNavigateToChangeOrders: (contractId: string, estimateId: string) => void;
  onOpenReport: (contract: Contract.AsObject) => void;
  onDeleteClick: (contract: Contract.AsObject) => void;
}

// Row component to handle the expandable functionality
const ContractRow = ({
  item,
  handleSetValue,
  onNavigateToChangeOrders,
  onOpenReport,
  onDeleteClick,
}: ContractRowProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const handleSetEstimateForChangeOrders = useHandleSetEstimateForChangeOrders();

  return (
    <>
      <StyledTableRow>
        <StyledTableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={e => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
        <StyledTableCell onClick={() => setOpen(!open)}>{item.customid}</StyledTableCell>
        <StyledTableCell onClick={() => setOpen(!open)}>{item.contractname}</StyledTableCell>
        <StyledTableCell>
          {item.associatedestimatesList?.length || 0} Estimate
          {item.associatedestimatesList?.length !== 1 ? 's' : ''}
        </StyledTableCell>
        <StyledTableCell>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip
              title={`Edit contract details for ${item.contractname || 'this contract'}`}
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  handleSetValue('update', item ?? null);
                }}
                color="primary"
                aria-label={`Edit contract ${item.contractname || 'unnamed contract'}`}
              >
                <EditIcon fontSize="small" aria-hidden="true" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={`Generate budget report for ${item.contractname || 'this contract'}`}
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onOpenReport(item);
                }}
                color="secondary"
                aria-label={`Generate report for contract ${item.contractname || 'unnamed contract'}`}
              >
                <AssessmentIcon fontSize="small" aria-hidden="true" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                item.associatedestimatesList && item.associatedestimatesList.length > 0
                  ? `Cannot delete contract with ${item.associatedestimatesList.length} associated estimate${item.associatedestimatesList.length !== 1 ? 's' : ''}`
                  : `Delete contract ${item.contractname || 'this contract'}`
              }
              arrow
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    onDeleteClick(item);
                  }}
                  color="error"
                  aria-label={`Delete contract ${item.contractname || 'unnamed contract'}`}
                  disabled={item.associatedestimatesList && item.associatedestimatesList.length > 0}
                >
                  <DeleteIcon fontSize="small" aria-hidden="true" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </StyledTableCell>
      </StyledTableRow>
      <TableRow
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
        }}
      >
        <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#3c3c3c !important' : '#e0e0e0 !important',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                component="div"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              >
                Associated Estimates
              </Typography>
              {item.associatedestimatesList?.length > 0 ? (
                <Table size="small" aria-label="associated estimates">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Estimate Name</StyledTableCell>
                      <StyledTableCell>ID</StyledTableCell>
                      <StyledTableCell>Division</StyledTableCell>
                      <StyledTableCell>Created Date</StyledTableCell>
                      <StyledTableCell sx={{ minWidth: '120px' }}>Change Orders</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.associatedestimatesList.map((estimate: AssociatedEstimate.AsObject) => (
                      <StyledTableRow
                        key={estimate.estimateid}
                        onClick={() => {
                          handleSetEstimateForChangeOrders(
                            item,
                            estimate,
                            onNavigateToChangeOrders
                          );
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <StyledTableCell component="th" scope="row">
                          {estimate.name || 'Unnamed Estimate'}
                        </StyledTableCell>
                        <StyledTableCell>{estimate.customid}</StyledTableCell>
                        <StyledTableCell>{estimate.masterformatdivisionname}</StyledTableCell>
                        <StyledTableCell>
                          {formatDateForDisplay(estimate.datecreated)}
                        </StyledTableCell>
                        <StyledTableCell>
                          <ChangeOrderStatusDisplay
                            changeOrdersList={estimate.associatedchangeordersList || []}
                            theme={theme}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">
                  No estimates associated with this contract
                </Typography>
              )}
            </Box>
          </Collapse>
        </StyledTableCell>
      </TableRow>
    </>
  );
};

export function ListContracts() {
  const theme = useTheme();

  const isViewingContractForm = useStore(useUnityBuildStore).isViewingContractForm;
  const setIsViewingContractForm = useStore(useUnityBuildStore).setIsViewingContractForm;
  const setIsViewingChangeOrderForm = useStore(useUnityBuildStore).setIsViewingChangeOrderForm;
  const setSelectedEstimateIdForChangeOrder =
    useStore(useUnityBuildStore).setSelectedEstimateIdForChangeOrder;

  // Local state for managing the selected estimate for change orders
  const [selectedEstimateForChangeOrders, setSelectedEstimateForChangeOrders] = useState<{
    contractId: string;
    estimateId: string;
  } | null>(null);

  // Local state for managing the contract budget report
  const [selectedContractForReport, setSelectedContractForReport] =
    useState<Contract.AsObject | null>(null);

  // States for contract budget report
  const [contractBudgetOpen, setContractBudgetOpen] = useState<boolean>(false);

  // Add state for success and error notifications
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract.AsObject | null>(null);

  // Delete mutation hook
  const deleteContractMutation = useDeleteContractMutation();

  // Fetch contract and error data
  const { data: contractList, error, isLoading } = useFetchContracts();
  const errorMessage = (error as Error)?.message;

  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(event.target.value);
  const handleSetValue = useHandleSetContract();

  const handleNavigateToChangeOrders = (contractId: string, estimateId: string) => {
    setSelectedEstimateForChangeOrders({ contractId, estimateId });
  };

  const handleOpenReport = (contract: Contract.AsObject) => {
    setSelectedContractForReport(contract);
    setContractBudgetOpen(true);
  };

  const handleCloseReport = () => {
    setContractBudgetOpen(false);
    setSelectedContractForReport(null);
  };

  // Notification handlers
  const handleCloseNotification = () => {
    setShowSuccessNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (contract: Contract.AsObject) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContractToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (contractToDelete) {
      try {
        await deleteContractMutation.mutateAsync(contractToDelete);
        setDeleteDialogOpen(false);
        setContractToDelete(null);
        setShowSuccessNotification(true);
        setSuccessMessage('Contract deleted successfully!');

        // Optimistic update handles immediate UI update, no manual refetch needed
      } catch (error) {
        console.error('Error deleting contract:', error);
        setDeleteDialogOpen(false);
        setContractToDelete(null);

        // Show user-friendly error message
        setShowErrorNotification(true);
        setDeleteErrorMessage(
          'This contract cannot be deleted. It may be referenced by other records in the system.'
        );
      }
    }
  };

  // Filter contracts based on the search term
  const filteredContracts: Contract.AsObject[] = (contractList ?? []).filter(
    (contracts: Contract.AsObject) =>
      contracts?.contractname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contracts?.contractdescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contracts?.customid?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCloseForm = () => {
    setIsViewingContractForm(false);
    setIsViewingChangeOrderForm(false);
  };

  return (
    <>
      {selectedEstimateForChangeOrders ? (
        <ListChangeOrders
          contractId={selectedEstimateForChangeOrders.contractId}
          estimateId={selectedEstimateForChangeOrders.estimateId}
          onBack={() => {
            setSelectedEstimateForChangeOrders(null);
            setSelectedEstimateIdForChangeOrder(null);
          }}
        />
      ) : (
        <Box sx={{ p: 2 }} role="region" aria-label="Contracts list">
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={event => {
                event.stopPropagation();
                handleSetValue('create', null);
              }}
              startIcon={<AddIcon aria-hidden="true" />}
              aria-label="Create new contract"
              sx={{ height: '40px' }}
            >
              Create Contract
            </Button>
            <TextField
              placeholder="Search Contracts"
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: <SearchIcon aria-hidden="true" sx={{ mr: 1 }} />,
              }}
              id="contract-search"
              aria-label="Search contracts"
              sx={{
                flexGrow: 1,
                minWidth: '250px',
                maxWidth: '400px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>

          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', my: 4 }}
              role="status"
              aria-label="Loading contracts"
            >
              <CircularProgress aria-label="Loading" />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': {
                  color: theme.palette.error.main,
                },
              }}
              role="alert"
            >
              Error: {errorMessage}
            </Alert>
          ) : filteredContracts && filteredContracts.length > 0 ? (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: '70vh',
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2c2c2c' : theme.palette.background.paper,
                boxShadow: theme.shadows[1],
                borderRadius: 1,
              }}
            >
              <Table stickyHeader aria-label="Contracts table" role="grid">
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '50px' }} />
                    <StyledTableCell>Contract ID</StyledTableCell>
                    <StyledTableCell sx={{ width: '60%' }}>Contract Name</StyledTableCell>
                    <StyledTableCell>Associated Estimates</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContracts.map(item => (
                    <ContractRow
                      key={item.contractid}
                      item={item}
                      handleSetValue={handleSetValue}
                      onNavigateToChangeOrders={handleNavigateToChangeOrders}
                      onOpenReport={handleOpenReport}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2c2c2c' : theme.palette.background.paper,
                borderRadius: 1,
                boxShadow: theme.shadows[1],
              }}
              role="status"
              aria-live="polite"
            >
              <Typography variant="body1" color="text.secondary">
                {searchTerm
                  ? 'No contracts match your search criteria.'
                  : 'No contracts available.'}
              </Typography>
            </Box>
          )}

          <Dialog
            open={isViewingContractForm}
            onClose={handleCloseForm}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2c2c2c' : theme.palette.background.paper,
                color: theme.palette.text.primary,
                maxHeight: '75%',
              },
            }}
            aria-labelledby="contract-dialog-title"
          >
            <DialogContent>{isViewingContractForm && <CreateContract />}</DialogContent>
          </Dialog>

          {/* Contract Budget Report Dialog - Managed independently */}
          {selectedContractForReport && (
            <ContractBudgetReport
              open={contractBudgetOpen}
              onClose={handleCloseReport}
              contract={selectedContractForReport}
            />
          )}

          {/* Success notification */}
          <Snackbar
            open={showSuccessNotification}
            autoHideDuration={4000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Error notification */}
          <Snackbar
            open={showErrorNotification}
            autoHideDuration={6000}
            onClose={handleCloseErrorNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseErrorNotification} severity="error" sx={{ width: '100%' }}>
              {deleteErrorMessage}
            </Alert>
          </Snackbar>

          {/* Delete confirmation dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
            PaperProps={{
              sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
            }}
          >
            <DialogTitle id="delete-dialog-title" sx={{ color: theme.palette.error.main }}>
              Delete Contract
            </DialogTitle>
            <DialogContent>
              <Typography id="delete-dialog-description" variant="body1">
                Are you sure you want to delete the contract "
                {contractToDelete?.contractname || 'Unnamed Contract'}"?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This action cannot be undone and will also delete all associated estimates and
                change orders.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                onClick={handleDeleteCancel}
                color="primary"
                variant="outlined"
                disabled={deleteContractMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={deleteContractMutation.isLoading}
                startIcon={
                  deleteContractMutation.isLoading ? <CircularProgress size={16} /> : undefined
                }
              >
                {deleteContractMutation.isLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
}
